import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useOpportunities } from '../contexts/OpportunityContext';
import { useAuth } from '../contexts/AuthContext';
import ContractCard from '../components/ContractCard';
import ContractTable from '../components/ContractTable';
import ContractFilters from '../components/ContractFilters';
import LoadingSpinner from '../components/LoadingSpinner';
import { ViewColumnsIcon, Squares2X2Icon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { canonicalUrlFor, metaDescription } from '../utils/seo';
import SEO from '../components/SEO';

const parseListParam = (value: string | null): string[] => {
  if (!value) return [];
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const parseRepeatedParam = (searchParams: URLSearchParams, key: string): string[] =>
  searchParams
    .getAll(key)
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeFiltersForUrl = (values: string[]): string[] => {
  if (!values?.length) return [];
  return [...values].filter(Boolean).sort((a, b) => a.localeCompare(b));
};

const Contracts: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    opportunities,
    filteredOpportunities, 
    loading, 
    error, 
    viewMode, 
    setViewMode, 
    loadOpportunities,
    appliedFilters,
    applyFilters,
    sortField,
    sortDirection,
    setSortField,
    setSortDirection
  } = useOpportunities();
  const { isAuthenticated, user } = useAuth();
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('keyword') ?? '');

  const filtersFromParams = React.useCallback(() => {
    const procuringOrganizations = parseListParam(searchParams.get('city'));
    const procuringTypes = parseListParam(searchParams.get('org_type'));
    const opportunityTypes = parseListParam(searchParams.get('opportunity_type'));
    const preBidMeeting = parseListParam(searchParams.get('pre_bid'));
    // Industry labels can contain commas, so each value uses its own query param.
    const industries = parseRepeatedParam(searchParams, 'industry');
    const keyword = searchParams.get('keyword') ?? '';
    return {
      procuringOrganizations,
      procuringTypes,
      opportunityTypes,
      preBidMeeting,
      industries,
      searchTerm: keyword,
    };
  }, [searchParams]);

  const syncUrl = React.useCallback(
    (filters: { industries: string[]; opportunityTypes: string[]; procuringTypes: string[]; preBidMeeting: string[]; procuringOrganizations: string[] }, keywordValue: string) => {
      const params = new URLSearchParams();
      const orgs = normalizeFiltersForUrl(filters.procuringOrganizations);
      const orgTypes = normalizeFiltersForUrl(filters.procuringTypes);
      const opportunityTypes = normalizeFiltersForUrl(filters.opportunityTypes);
      const preBid = normalizeFiltersForUrl(filters.preBidMeeting);
      const industries = normalizeFiltersForUrl(filters.industries);
      if (orgs.length) params.set('city', orgs.join(','));
      if (orgTypes.length) params.set('org_type', orgTypes.join(','));
      if (opportunityTypes.length) params.set('opportunity_type', opportunityTypes.join(','));
      if (preBid.length) params.set('pre_bid', preBid.join(','));
      industries.forEach((industry) => params.append('industry', industry));
      if (keywordValue?.trim()) params.set('keyword', keywordValue.trim());
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  useEffect(() => {
    loadOpportunities('browse');
  }, [loadOpportunities]);

  useEffect(() => {
    setSearchTerm(searchParams.get('keyword') ?? '');
  }, [searchParams]);

  useEffect(() => {
    if (!opportunities.length) return;
    const parsed = filtersFromParams();
    applyFilters(parsed);
  }, [opportunities, filtersFromParams, applyFilters]);


  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const cardSortOptions = [
    { value: 'due_date:asc', label: 'Due soonest', field: 'due_date', direction: 'asc' as const },
    { value: 'due_date:desc', label: 'Due latest', field: 'due_date', direction: 'desc' as const },
    { value: 'release_date:desc', label: 'Recently posted', field: 'release_date', direction: 'desc' as const },
    { value: 'release_date:asc', label: 'Oldest posted', field: 'release_date', direction: 'asc' as const },
  ];

  const currentCardSort = cardSortOptions.find(
    (option) => option.field === sortField && option.direction === sortDirection
  ) ?? cardSortOptions[0];

  const handleCardSortChange = (value: string) => {
    const option = cardSortOptions.find((item) => item.value === value);
    if (!option) return;
    setSortField(option.field);
    setSortDirection(option.direction);
  };

  const handleFilterChange = (filters: {
    industries: string[];
    opportunityTypes: string[];
    procuringTypes: string[];
    preBidMeeting: string[];
    procuringOrganizations: string[];
  }) => {
    applyFilters({
      ...filters,
      searchTerm,
    });
    syncUrl(filters, searchTerm);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    applyFilters({
      ...appliedFilters,
      searchTerm: value,
    });
    syncUrl(
      {
        industries: appliedFilters.industries,
        opportunityTypes: appliedFilters.opportunityTypes,
        procuringTypes: appliedFilters.procuringTypes,
        preBidMeeting: appliedFilters.preBidMeeting,
        procuringOrganizations: appliedFilters.procuringOrganizations,
      },
      value
    );
  };

  const totalOpportunities = useMemo(() => opportunities.length, [opportunities]);
  const filtersDisabled = !isAuthenticated;

  if (loading && filteredOpportunities.length === 0) {
    return <LoadingSpinner message="Loading opportunities..." />;
  }

  return (
    <div className="space-y-8">
      <SEO
        title="Browse Opportunities – OppFoundry"
        description={metaDescription('Browse and filter active municipal opportunities by industry, procuring organization, and due date.')}
        canonical={canonicalUrlFor()}
        og={{
          title: 'Browse Opportunities – OppFoundry',
          description: metaDescription('Find opportunities relevant to your service business.'),
          url: canonicalUrlFor() ?? undefined,
        }}
      />
      {/* Header */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">Contract Opportunities</h1>
          <p className="mt-3 max-w-2xl text-base text-gray-600">
            Find opportunities for your business in Massachusetts.
          </p>
        </div>
        {isAuthenticated && user?.is_premium && null}
      </div>

      {!isAuthenticated && (
        <div className="rounded-2xl bg-blue-700 p-6 text-white shadow-lg">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold">Create a free account</h2>
              <p className="mt-1 text-sm text-blue-100">
                Unlock advanced filters, save preferences, and get personalised opportunity recommendations.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-blue-600 shadow-sm transition hover:bg-blue-50"
              >
                Sign Up Free
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-full border border-blue-100 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Filter Opportunities</h2>
            <p className="text-sm text-gray-600">
              {filtersDisabled
                ? 'Sign in to unlock advanced filtering and saved preferences.'
                : 'Refine your view by industry, procuring organization, or keyword.'}
            </p>
          </div>
          <div className="text-sm text-gray-500">
            Showing {filteredOpportunities.length} of {totalOpportunities} opportunities
          </div>
        </div>

        <div className="mt-6 space-y-5">
          <div className="max-w-2xl">
            <div className="flex flex-col gap-2">
              <label htmlFor="contract-search" className="block text-sm font-medium text-gray-700">
                Search opportunities
              </label>
              <div className="relative h-12">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </span>
                <input
                  id="contract-search"
                  type="text"
                  disabled={filtersDisabled}
                  className={`block h-full w-full rounded-lg border border-gray-300 bg-white pl-10 pr-4 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    filtersDisabled ? 'bg-gray-50 text-gray-400' : ''
                  }`}
                  placeholder="Search opportunities..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div>
            <ContractFilters
              onFilterChange={handleFilterChange}
              appliedFilters={appliedFilters}
              disabled={filtersDisabled}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-500">View:</span>
            <div className="flex overflow-hidden rounded-full border border-gray-200 bg-white p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${
                  viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <ViewColumnsIcon className="h-4 w-4" />
                Table
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${
                  viewMode === 'cards' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Squares2X2Icon className="h-4 w-4" />
                Cards
              </button>
            </div>
          </div>
          {viewMode === 'cards' ? (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-500">Sort:</span>
              <select
                value={currentCardSort.value}
                onChange={(event) => handleCardSortChange(event.target.value)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {cardSortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <span className="text-xs text-gray-400">Tap a column header to sort</span>
          )}
        </div>
        <div className="text-sm text-gray-500">
          {filteredOpportunities.length} opportunities
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Contract List */}
      {filteredOpportunities.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500">
            {searchTerm ||
            appliedFilters.industries.length > 0 ||
            appliedFilters.opportunityTypes.length > 0 ||
            appliedFilters.procuringTypes.length > 0 ||
            appliedFilters.preBidMeeting.length > 0 ||
            appliedFilters.procuringOrganizations.length > 0
              ? 'No opportunities match your current filters.'
              : 'No opportunities available at the moment.'}
          </div>
        </div>
      ) : (
        <>
          {viewMode === 'cards' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredOpportunities.map((contract, index) => (
                <ContractCard
                  key={`${contract.title}-${index}`}
                  contract={contract}
                  browseSearch={location.search}
                  showMatchReasons={Boolean(isAuthenticated && user?.is_premium)}
                />
              ))}
            </div>
          ) : (
            <ContractTable 
              contracts={filteredOpportunities}
              onSort={handleSort}
              sortField={sortField}
              sortDirection={sortDirection}
              browseSearch={location.search}
              showMatchReasons={Boolean(isAuthenticated && user?.is_premium)}
            />
          )}
        </>
      )}

      {/* Loading overlay for additional loads */}
      {loading && filteredOpportunities.length > 0 && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      )}
    </div>
  );
};

export default Contracts;
